/**
 * phase-documents-pane-split-content-sized — derive the File Name column
 * width from the currently-visible filenames so, with the preview pane open,
 * leftover width goes to the PDF pane instead of dead whitespace in the table.
 *
 * Pure + deterministic (no DOM/canvas): the width is a DEFAULT the user can
 * override by dragging the divider, so a proportional-font estimate is plenty
 * and, unlike canvas `measureText`, it is unit-testable and jsdom-safe. The
 * estimate is deliberately a touch generous (CHAR_PX) so names don't clip
 * before the ellipsis cap engages.
 */

/** Estimated px per character at the table's ~14px proportional font. */
export const CHAR_PX = 8;
/** Cell horizontal padding (tdStyle 8+8) plus a little slack. */
export const CELL_PADDING = 24;
/** Readable floor — never collapse the column below this. */
export const DEFAULT_MIN_FILE_WIDTH = 200;
/** Below this movement the boundary does not shift (anti-jitter). */
export const JITTER_THRESHOLD = 24;

export interface DeriveFileColumnWidthOptions {
  /** Readable floor (default DEFAULT_MIN_FILE_WIDTH). */
  readonly minWidth?: number;
  /** Hard cap — beyond this the name truncates with ellipsis + title tooltip.
   *  Caller passes ~40% of the available split width. */
  readonly maxWidth: number;
}

/**
 * The content-sized File Name column width for a set of visible filenames,
 * clamped to [minWidth, maxWidth]. Empty set → minWidth (a bare table still
 * reads sensibly). Derive this ONCE per data change (names/sort/filter/page),
 * not per scroll or hover.
 */
export function deriveFileColumnWidth(
  filenames: readonly string[],
  { minWidth = DEFAULT_MIN_FILE_WIDTH, maxWidth }: DeriveFileColumnWidthOptions,
): number {
  const floor = Math.min(minWidth, maxWidth);
  if (filenames.length === 0) return floor;
  let longest = 0;
  for (const name of filenames) {
    if (name.length > longest) longest = name.length;
  }
  const content = longest * CHAR_PX + CELL_PADDING;
  return Math.max(floor, Math.min(maxWidth, content));
}

/**
 * Anti-jitter gate: keep the current width unless it is unset or the newly
 * derived target moves it by at least JITTER_THRESHOLD. Same data → same
 * width; a one-character change won't nudge the boundary.
 */
export function nextFileColumnWidth(current: number | null, target: number): number {
  if (current === null) return target;
  return Math.abs(target - current) >= JITTER_THRESHOLD ? target : current;
}
